import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Search, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'components/core/theme-provider';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';
import { useDebounce } from 'hooks/use-debounce';
import { AGENT_SKILLS_PAGE_SIZE, useGetAgentSkillsInfinite } from 'pages/skills/use-skills';
import { getSkillDisplayName, type SkillListItem } from 'pages/skills/skills.types';
import { getPickerTheme } from 'pages/agents/utils/agent-config-picker-theme';

type AgentConfigAddSkillModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedSkillIds: string[];
  onAddSkill: (skill: SkillListItem) => void;
  onRemoveSkill: (skillId: string) => void;
};

function SkillRowSkeleton({ fieldClass }: { fieldClass: string }) {
  return (
    <div className={cn('flex items-center gap-3 rounded-2xl border p-4', fieldClass)}>
      <Skeleton className="h-11 w-11 shrink-0 rounded-2xl" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-48" />
      </div>
      <Skeleton className="h-8 w-16 rounded-full" />
    </div>
  );
}

export function AgentConfigAddSkillModal({
  open,
  onOpenChange,
  selectedSkillIds,
  onAddSkill,
  onRemoveSkill,
}: AgentConfigAddSkillModalProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const ui = getPickerTheme(theme === 'dark');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAgentSkillsInfinite(
    {
      pageSize: AGENT_SKILLS_PAGE_SIZE,
      search_query: debouncedSearch.trim() || undefined,
    },
    { enabled: open, scope: 'agents-v2-config-modal' }
  );

  const skills = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data?.pages]
  );
  const selectedSkills = skills.filter((skill) => selectedSkillIds.includes(skill.id));
  const selectedCount = selectedSkillIds.length;

  useEffect(() => {
    const scrollContainer = listContainerRef.current;
    const loadMoreEl = loadMoreRef.current;
    if (!open || !scrollContainer || !loadMoreEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: scrollContainer,
        rootMargin: '200px',
        threshold: 0.1,
      }
    );

    observer.observe(loadMoreEl);
    return () => observer.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage, skills.length, isLoading]);

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSearchQuery('');
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        hideClose
        className={cn(
          'flex h-[min(92dvh,720px)] w-[calc(100vw-2rem)] max-w-[640px] flex-col gap-0 overflow-hidden rounded-3xl p-0 shadow-2xl',
          ui.dialog
        )}
      >
        <DialogTitle className="sr-only">
          {t('ADD_SKILL', { defaultValue: 'Add skill' })}
        </DialogTitle>

        <div className={cn('relative shrink-0 px-6 pb-5 pt-6', ui.header)}>
          <button
            type="button"
            onClick={() => handleClose(false)}
            className={cn('absolute right-4 top-4 rounded-full p-1.5 transition-colors', ui.close)}
            aria-label={t('CLOSE', 'Close')}
          >
            <X className="h-4 w-4" />
          </button>

          <p className={cn('pr-8 font-sans text-[1.65rem] font-bold leading-tight', ui.title)}>
            {t('ADD_SKILL', { defaultValue: 'Add skill' })}
          </p>
          <p className={cn('mt-2 pr-8 font-sans text-sm leading-5', ui.subtitle)}>
            {t('BROWSE_AND_SELECT_SKILLS_FOR_AGENT', {
              defaultValue:
                'Browse and select skills compatible with Agents V2 to add to your agent.',
            })}
          </p>

          <div className="relative mt-5">
            <Search
              className={cn(
                'pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2',
                ui.searchIcon
              )}
            />
            <input
              type="search"
              placeholder={t('SEARCH_SKILLS', { defaultValue: 'Search skills...' })}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className={cn(
                'h-12 w-full rounded-2xl border pl-11 pr-4 text-sm outline-none transition-colors',
                ui.search
              )}
            />
          </div>
        </div>

        {selectedSkills.length > 0 && (
          <div className={cn('flex shrink-0 flex-wrap gap-2 border-b px-6 pb-4 pt-4', ui.chipWrap)}>
            {selectedSkills.map((skill) => (
              <button
                key={skill.id}
                type="button"
                onClick={() => onRemoveSkill(skill.id)}
                className={cn(
                  'inline-flex max-w-full items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                  ui.chip
                )}
              >
                <span className="truncate">{getSkillDisplayName(skill)}</span>
                <X className="h-3 w-3 shrink-0" />
              </button>
            ))}
          </div>
        )}

        <div
          ref={listContainerRef}
          className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain px-6 py-4"
        >
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <SkillRowSkeleton key={`skill-skeleton-${index}`} fieldClass={ui.field} />
            ))
          ) : skills.length === 0 ? (
            <div className="flex h-full min-h-[220px] flex-col items-center justify-center text-center">
              <div
                className={cn(
                  'mb-3 flex h-14 w-14 items-center justify-center rounded-full',
                  ui.emptyIcon
                )}
              >
                <Sparkles className="h-6 w-6" />
              </div>
              <p className={cn('text-sm font-medium', ui.body)}>
                {searchQuery.trim()
                  ? t('NO_SKILLS_FOUND_MATCHING_SEARCH', {
                      defaultValue: 'No skills found matching your search',
                    })
                  : t('NO_SKILLS_AVAILABLE', { defaultValue: 'No skills available' })}
              </p>
            </div>
          ) : (
            <>
              {skills.map((skill) => {
                const isSelected = selectedSkillIds.includes(skill.id);

                return (
                  <button
                    key={skill.id}
                    type="button"
                    onClick={() => (isSelected ? onRemoveSkill(skill.id) : onAddSkill(skill))}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-colors',
                      isSelected ? ui.rowSelected : ui.rowIdle
                    )}
                  >
                    {skill.iconUrl ? (
                      <img
                        src={skill.iconUrl}
                        alt=""
                        className="h-11 w-11 shrink-0 rounded-2xl object-cover"
                      />
                    ) : (
                      <div
                        className={cn(
                          'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl',
                          isSelected ? ui.iconSelected : ui.iconIdle
                        )}
                      >
                        <Sparkles className="h-5 w-5" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className={cn('truncate font-medium', ui.body)}>
                        {getSkillDisplayName(skill)}
                      </div>
                      <div className={cn('line-clamp-2 break-words text-sm', ui.muted)}>
                        {skill.description ||
                          t('NO_DESCRIPTION', { defaultValue: 'No description' })}
                      </div>
                    </div>
                    <span
                      className={cn(
                        'inline-flex h-8 shrink-0 items-center gap-1 rounded-full px-3 text-xs font-semibold',
                        isSelected ? ui.pillRemove : ui.pillIdle
                      )}
                    >
                      {isSelected ? <Check className="h-3.5 w-3.5" /> : null}
                      {isSelected
                        ? t('REMOVE', { defaultValue: 'Remove' })
                        : t('ADD', { defaultValue: 'Add' })}
                    </span>
                  </button>
                );
              })}

              {isFetchingNextPage &&
                Array.from({ length: 2 }).map((_, index) => (
                  <SkillRowSkeleton key={`skill-load-more-${index}`} fieldClass={ui.field} />
                ))}

              {hasNextPage && (
                <div ref={loadMoreRef} className="h-1 w-full shrink-0" aria-hidden />
              )}
            </>
          )}
        </div>

        <div className={cn('shrink-0 border-t px-6 py-4', ui.footer)}>
          <p className={cn('text-sm', ui.muted)}>
            {selectedCount}{' '}
            {selectedCount === 1
              ? t('SKILL_SELECTED', { defaultValue: 'skill selected' })
              : t('SKILLS_SELECTED', { defaultValue: 'skills selected' })}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
